import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { isObject } from 'lodash'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../../reducers/UserReducer'
import InternshipsRequestsTable from './InternshipsRequestsTable'
import {
  deleteInternshipRequest, getAllInternshipRequests, refuseInternshipRequest, updateInternshipRequest
} from '../../../../utils/api/internshipRequest'
import { onError, onSuccess } from '../../../../utils/apiHelper'
import { getIdOrNull } from '../../../../utils'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import { OrderBy, OrderDirections } from '../../../../utils/constants'
import InstitutionModal from '../../../../Providers/Forms/InstitutionModal/InstitutionModal'
import { SchoolContext } from '../../../../Providers/SchoolProvider'
import { GlobalContext } from '../../../../Providers/GlobalProvider'
import { collapseStateGroups, expandStateGroups, StateTypes } from '../../../../utils/entities/internshipRequest'
import { useLocalStorage } from '../../../../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS from '../../../../utils/localStorage'
import InternshipRequestRefusalModal from './InternshipRequestRefusalModal'
import { retrieveMetadata } from '../../../../utils/http'

const { key: initChecked } = StateTypes.OPEN

const reconcileStoredParameters = parameters => ({
  ...parameters,
  filters: { ...parameters?.filters, states: collapseStateGroups(parameters?.filters?.states ?? []) }
})

const InternshipRequestsView = () => {
  const sc = useContext(SchoolContext)
  const { countries } = useContext(GlobalContext)

  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)
  const [requestToRefuse, setRequestToRefuse] = useState(null)
  const [refusalLoading, setRefusalLoading] = useState(false)

  const [internshipRequests, setInterneshipRequests] = useState({ data: [], metadata: {} })
  const [loading, setLoading] = useState(true)
  const [rawParameters, setRawParameters] = useLocalStorage(
    LOCAL_STORAGE_KEYS.INTERNSHIPS_REQUEST.PARAMETERS,
    new TableParameters({ filters: { states: [initChecked] }, order: { by: OrderBy.CREATED_AT, direction: OrderDirections.DESC }, pageSize: 20 }),
    reconcileStoredParameters
  )

  const parameters = useMemo(() => new TableParameters({ ...rawParameters, metadata: internshipRequests.metadata }), [rawParameters, internshipRequests.metadata])

  const fetchInternshipRequests = useCallback(parameters => {
    setLoading(true)
    const queryParameters = new TableParameters({
      ...parameters,
      filters: { ...parameters.filters, states: expandStateGroups(parameters.filters?.states ?? []) }
    })
    getAllInternshipRequests(user, { ...queryParameters.toQueryParameters(), contexts: ['institution', 'school', 'documents'] }).then(({ data, meta }) => {
      setInterneshipRequests({ data, metadata: retrieveMetadata(meta) })
    }).finally(() => setLoading(false))
  }, [user.id])

  useEffect(() => {
    fetchInternshipRequests(parameters)
  }, [fetchInternshipRequests, parameters.filters, parameters.page, parameters.order, parameters.pageSize])

  const handleDelete = useCallback(request => {
    deleteInternshipRequest(user, request).then(json => {
      onSuccess(t('internship_request.delete.success'))
      fetchInternshipRequests(parameters)
    }).catch(() => {
      onError(t('internship_request.delete.error'))
    })
  }, [user, t, fetchInternshipRequests, parameters])

  const handleRefuse = useCallback((request, refusalReason = '') => {
    setRefusalLoading(true)

    refuseInternshipRequest(user, request, refusalReason).then(json => {
      if (json?.data) {
        onSuccess(t('internship_request.refuse.success'))
        setRequestToRefuse(null)
        fetchInternshipRequests(parameters)
      } else {
        onError(t('internship_request.refuse.error'))
      }
    }).catch(() => {
      onError(t('internship_request.refuse.error'))
    }).finally(() => {
      setRefusalLoading(false)
    })
  }, [user, t, fetchInternshipRequests, parameters])

  const handleLinkInstitution = useCallback((request, institution, sector) => {
    const body = { id: request.id, institution: institution.id }

    if (isObject(sector)) {
      body.sector = sector.id
    }

    updateInternshipRequest(user, body).then(() => {
      onSuccess(t('internship_request.update_institution.success'))
      fetchInternshipRequests(parameters)
    }).catch(() => {
      onError(t('internship_request.update_institution.error'))
    }).finally(() => {
      /** The creation hands the country back as a bare id : the list this joins holds the entity every column reads. */
      const country = countries.find(c => c.id === getIdOrNull(institution.country)) ?? null

      sc.setInstitutions(data => data.some(i => i.id === institution.id) ? data : [...data, { ...institution, country }])
    })
  }, [user, fetchInternshipRequests, parameters, sc, countries])

  return (
    <div>
      <InstitutionModal>
        <InternshipsRequestsTable
          data={internshipRequests.data}
          institution={user?.institutions[0]}
          school={user?.school}
          initChecked={initChecked}
          loading={loading}
          metadata={internshipRequests.metadata}
          parameters={parameters}
          onDelete={handleDelete}
          onRefuse={setRequestToRefuse}
          onParametersChange={newParameters => setRawParameters(oldParameters => ({ ...oldParameters, ...newParameters }))}
          onLinkInstitution={handleLinkInstitution}
          reload={() => fetchInternshipRequests(parameters)}
        />
        <InternshipRequestRefusalModal
          visible={requestToRefuse != null}
          loading={refusalLoading}
          onCancel={() => setRequestToRefuse(null)}
          onConfirm={refusalReason => handleRefuse(requestToRefuse, refusalReason)}
        />
      </InstitutionModal>
    </div>
  )
}

export default InternshipRequestsView
